import moment from 'moment';
import { TF } from 'react-translator-component';

import {
  DoorAttemptStatus,
  DoorBodyType,
  DoorConfig,
  DoorConfigPayload,
  DoorHeader,
  DoorHttpMethod,
  DoorOpening,
  DoorSummary,
  DoorTransport,
} from '../../../../types/responses/DoorAccess';

export interface DoorFormValues {
  enabled: boolean;
  transport: DoorTransport;
  deviceId: string;
  method: DoorHttpMethod;
  url: string;
  headers: DoorHeader[];
  bodyType: DoorBodyType;
  body: string;
}

type DoorConnectorStatus = 'DISABLED' | 'NOT_CONFIGURED' | 'CONFIGURED';

export const emptyHeaderRow = (): DoorHeader => ({
  key: '',
  value: '',
  description: '',
});

export const isHeaderRowEmpty = (row: DoorHeader): boolean =>
  !row.key.trim() && !row.value.trim() && !row.description.trim();

export const countFilledHeaders = (rows: DoorHeader[]): number =>
  rows.filter((row) => !isHeaderRowEmpty(row)).length;

const configuredFormValues = (config: DoorConfig): DoorFormValues => ({
  enabled: !!config.enabled,
  transport: config.transport || 'HTTP',
  deviceId: config.deviceId || '',
  method: config.method || 'POST',
  url: config.url || '',
  headers: (config.headers || []).map((header) => ({
    key: header.key || '',
    value: header.value || '',
    description: header.description || '',
  })),
  bodyType: 'JSON',
  body: config.body || '',
});

// Conector sem configuração já vem com o contrato do controlador movE IoT (mk-fw-iot):
// POST /unlock, DEVICE_ID no X-Device-Id e o conector no corpo. Falta só o DEVICE_ID.
export const MOVE_IOT_DEFAULTS: DoorFormValues = {
  enabled: false,
  transport: 'HTTP',
  deviceId: '',
  method: 'POST',
  url: 'http://localhost:8180/unlock',
  headers: [
    {
      key: 'X-Device-Id',
      value: '',
      description: 'DEVICE_ID do controlador (página /config)',
    },
  ],
  bodyType: 'JSON',
  body: '{\n  "connector_id": {connectorId}\n}',
};

// Método movE IoT não tem corpo: o CS já sabe o comando de destravar. Ao voltar para o webhook,
// um corpo vazio (ou o antigo corpo do movE IoT) volta ao padrão.
const LEGACY_MOVE_IOT_BODY = /"action"\s*:\s*"unlock"/;

export const switchTransport = (
  values: DoorFormValues,
  transport: DoorTransport,
): DoorFormValues => {
  const body = values.body.trim();
  const resetBody =
    transport === 'HTTP' && (!body || LEGACY_MOVE_IOT_BODY.test(body));
  return {
    ...values,
    transport,
    body: resetBody ? MOVE_IOT_DEFAULTS.body : values.body,
  };
};

const isUnconfigured = (config: DoorConfig): boolean =>
  !config.url && !config.deviceId && !config.headers?.length && !config.body;

export const toFormValues = (config: DoorConfig): DoorFormValues =>
  isUnconfigured(config)
    ? {
        ...MOVE_IOT_DEFAULTS,
        headers: MOVE_IOT_DEFAULTS.headers.map((header) => ({ ...header })),
        enabled: !!config.enabled,
      }
    : configuredFormValues(config);

export const toPayload = (values: DoorFormValues): DoorConfigPayload => ({
  enabled: values.enabled,
  transport: values.transport,
  deviceId: values.deviceId.trim(),
  method: values.method,
  url: values.url.trim(),
  headers: values.headers
    .filter((row) => !isHeaderRowEmpty(row))
    .map((row) => ({
      key: row.key.trim(),
      value: row.value,
      description: row.description.trim(),
    })),
  bodyType: values.bodyType,
  body: values.transport === 'MOVE_IOT' ? '' : values.body,
});

export const copyFromConfig = (source: DoorConfig): DoorFormValues => ({
  ...toFormValues(source),
  enabled: true,
});

export const summaryFromConfig = (
  connectorId: number,
  config: DoorConfig,
): DoorSummary => ({
  connectorId,
  enabled: !!config.enabled,
  configured:
    config.transport === 'MOVE_IOT' ? !!config.deviceId : !!config.url,
});

export const connectorStatus = (summary?: DoorSummary): DoorConnectorStatus => {
  if (!summary || !summary.enabled) return 'DISABLED';
  return summary.configured ? 'CONFIGURED' : 'NOT_CONFIGURED';
};

export const attemptStatusLabel = (
  status: DoorAttemptStatus,
  httpStatus: number | null,
): string => {
  switch (status) {
    case 'SUCCESS':
      return `${httpStatus ?? ''} OK`.trim();
    case 'UNEXPECTED_STATUS':
      return httpStatus == null
        ? TF('Unexpected response')
        : TF('Unexpected code {0}', httpStatus);
    case 'TIMEOUT':
      return TF('Timed out');
    default:
      return TF('Network error');
  }
};

export const openingLine = (opening: DoorOpening): string =>
  [
    moment(opening.at).format('DD/MM/YYYY HH:mm:ss'),
    opening.origin === 'APP' ? TF('Driver app') : TF('Dashboard'),
    opening.userName,
    opening.status
      ? attemptStatusLabel(opening.status, opening.httpStatus)
      : '—',
  ]
    .filter(Boolean)
    .join(' · ');
